import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { LanguagePicker } from "./LanguagePicker";
import { api } from "../lib/api";
import type { User } from "../lib/types";

interface Props {
  /** Absent on the pages that are readable signed out, such as the leaderboard. */
  user?: User;
  children: ReactNode;
}

const NAV_ITEMS = [
  { to: "/", key: "nav.sessions", signedInOnly: true, adminOnly: false },
  { to: "/leaderboard", key: "nav.leaderboard", signedInOnly: false, adminOnly: false },
  { to: "/teams", key: "nav.teams", signedInOnly: true, adminOnly: false },
  { to: "/upload", key: "nav.upload", signedInOnly: true, adminOnly: false },
  { to: "/admin", key: "nav.admin", signedInOnly: true, adminOnly: true },
];

export function Layout({ user, children }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => queryClient.clear(),
  });

  return (
    <div className="min-h-screen bg-zinc-950">
      <header className="border-b border-zinc-800/80 bg-zinc-900/40 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-8 px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-semibold tracking-tight">LMU Stats</span>
            <span className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-400">
              {t("app.beta")}
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {NAV_ITEMS.filter(
              (item) =>
                (user || !item.signedInOnly) && (!item.adminOnly || user?.is_admin),
            ).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    isActive
                      ? "bg-zinc-800 text-zinc-100"
                      : "text-zinc-400 hover:text-zinc-100",
                  ].join(" ")
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <LanguagePicker />
            {user ? (
              <>
                {user.avatar_url && (
                  <img
                    src={user.avatar_url}
                    alt=""
                    className="size-8 rounded-full ring-1 ring-zinc-700"
                  />
                )}
                <span className="text-sm text-zinc-300">{user.persona_name}</span>
                <button
                  type="button"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-100 disabled:opacity-50"
                >
                  {t("nav.signOut")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:border-zinc-500"
              >
                {t("nav.signIn")}
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">{children}</main>
    </div>
  );
}
